<!-- ═══════════════════════════════════════════════════════════════════════════
     LANDING V2 — Predivent
     Proposition de valeur unifiée : fixe + itinérant
     ═══════════════════════════════════════════════════════════════════════════ -->

<!-- ─── HERO ──────────────────────────────────────────────────────────────── -->
<section class="hero-v2 relative overflow-hidden">
  <!-- Animated mesh background -->
  <div class="hero-mesh" aria-hidden="true"></div>
  <div class="hero-glow hero-glow-1" aria-hidden="true"></div>
  <div class="hero-glow hero-glow-2" aria-hidden="true"></div>
  <div class="hero-glow hero-glow-3" aria-hidden="true"></div>
  <!-- Floating food icons -->
  <div class="hero-floaters" aria-hidden="true">
    <span class="floater" style="--delay: 0s; --x: 8%; --y: 20%">🥖</span>
    <span class="floater" style="--delay: 1.5s; --x: 85%; --y: 30%">🍔</span>
    <span class="floater" style="--delay: 3s; --x: 15%; --y: 70%">🥐</span>
    <span class="floater" style="--delay: 0.8s; --x: 78%; --y: 75%">🍕</span>
    <span class="floater" style="--delay: 2.2s; --x: 50%; --y: 15%">🧁</span>
    <span class="floater" style="--delay: 4s; --x: 92%; --y: 55%">🥗</span>
    <span class="floater" style="--delay: 2.8s; --x: 5%; --y: 45%">🌮</span>
  </div>

  <div class="max-w-7xl mx-auto px-4 pt-28 pb-20 relative z-10">
    <!-- Badge -->
    <div class="text-center mb-6">
      <span
        class="hero-badge inline-flex items-center gap-2 text-white text-sm font-semibold px-5 py-2.5 rounded-full"
      >
        <span
          class="inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse"
        ></span>
        Nouveau : recommandations intelligentes pour 13 métiers
      </span>
    </div>

    <!-- Headline -->
    <h1
      class="text-center text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white leading-tight max-w-5xl mx-auto"
    >
      Préparez la
      <span class="hero-highlight"> bonne quantité </span>
      <br class="hidden sm:block" />
      chaque jour
    </h1>

    <!-- Sub-headline -->
    <p
      class="text-center text-lg sm:text-xl text-purple-100 mt-6 max-w-3xl mx-auto leading-relaxed"
    >
      L'IA qui vous dit
      <strong class="text-white">quoi et combien préparer</strong> —
      en&nbsp;2&nbsp;minutes.
      <br class="hidden sm:block" />
      Moins de pertes, zéro rupture, plus de marge. Pour les commerçants fixes
      <em>et</em> itinérants.
    </p>

    <!-- Mode toggle -->
    <div class="flex justify-center mt-8">
      <div
        class="inline-flex bg-white/10 backdrop-blur-md rounded-full p-1 border border-white/20"
      >
        <button
          (click)="toggleMode('itinerant')"
          [class]="
            mode() === 'itinerant'
              ? 'bg-white text-purple-700 shadow-lg'
              : 'text-white hover:bg-white/20'
          "
          class="px-6 py-2.5 rounded-full font-semibold text-sm transition-all duration-300"
        >
          🚚 Itinérant
        </button>
        <button
          (click)="toggleMode('fixed')"
          [class]="
            mode() === 'fixed'
              ? 'bg-white text-purple-700 shadow-lg'
              : 'text-white hover:bg-white/20'
          "
          class="px-6 py-2.5 rounded-full font-semibold text-sm transition-all duration-300"
        >
          🏪 Commerce fixe
        </button>
      </div>
    </div>

    <!-- CTA buttons -->
    <div class="flex flex-col sm:flex-row justify-center gap-4 mt-10">
      <a
        routerLink="/register"
        class="cta-primary inline-flex items-center justify-center gap-2 bg-white text-purple-700 px-8 py-4 rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl transform hover:-translate-y-0.5 transition-all duration-300"
      >
        🚀 Essai gratuit 1 mois
      </a>
      <a
        href="#demo"
        class="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-xl font-bold text-lg border-2 border-white/30 hover:bg-white/20 transition-all duration-300"
      >
        ▶ Découvrir la solution en vidéo
      </a>
    </div>

    <!-- Trust line -->
    <div
      class="flex flex-wrap justify-center gap-6 mt-8 text-sm text-purple-200"
    >
      <span>✓ Sans carte bancaire</span>
      <span>✓ Prêt en 5 minutes</span>
      <span>✓ Annulation 1 clic</span>
      <span>✓ Données 100% françaises</span>
    </div>

    <!-- Hero preview card — contextual -->
    <div class="mt-14 max-w-2xl mx-auto">
      <!-- ITINERANT preview -->
      @if (mode() === "itinerant") {
        <div
          class="hero-card bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-6 sm:p-8 border border-gray-100 dark:border-slate-700"
        >
          <div class="flex items-center justify-between mb-4">
            <div>
              <h3 class="font-bold text-gray-900 dark:text-white text-lg">
                Marché de Noël • Antibes
              </h3>
              <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                📅 Sam 15 déc • ⏰ 10h–18h • ☀️ 12°C ensoleillé
              </p>
            </div>
            <span
              class="bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 px-3 py-1 rounded-full text-xs font-bold"
              >IA Prête</span
            >
          </div>
          <div class="grid grid-cols-3 gap-3 mb-4">
            <div
              class="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-3 text-center"
            >
              <div
                class="text-2xl font-extrabold text-purple-700 dark:text-purple-300"
              >
                48
              </div>
              <div class="text-xs text-gray-600 dark:text-gray-400 mt-1">
                🍔 Burgers
              </div>
            </div>
            <div
              class="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-3 text-center"
            >
              <div
                class="text-2xl font-extrabold text-purple-700 dark:text-purple-300"
              >
                35
              </div>
              <div class="text-xs text-gray-600 dark:text-gray-400 mt-1">
                🌮 Tacos
              </div>
            </div>
            <div
              class="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-3 text-center"
            >
              <div
                class="text-2xl font-extrabold text-purple-700 dark:text-purple-300"
              >
                60
              </div>
              <div class="text-xs text-gray-600 dark:text-gray-400 mt-1">
                🍟 Frites
              </div>
            </div>
          </div>
          <div
            class="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-3 text-sm text-gray-600 dark:text-gray-400"
          >
            💡 <strong>Pourquoi ces quantités ?</strong> Soleil (+20%), samedi
            (+15%), marché de Noël (+25%), basé sur 3 événements similaires.
          </div>
        </div>
      }

      <!-- FIXED preview -->
      @if (mode() === "fixed") {
        <div
          class="hero-card bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-6 sm:p-8 border border-gray-100 dark:border-slate-700"
        >
          <div class="flex items-center justify-between mb-4">
            <div>
              <h3 class="font-bold text-gray-900 dark:text-white text-lg">
                Boulangerie du Cours • Lundi
              </h3>
              <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                📅 Lun 16 déc • 🌧️ Pluie 8°C • Zone centre-ville
              </p>
            </div>
            <span
              class="bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 px-3 py-1 rounded-full text-xs font-bold"
              >IA Prête</span
            >
          </div>
          <div class="grid grid-cols-3 gap-3 mb-4">
            <div
              class="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-3 text-center"
            >
              <div
                class="text-2xl font-extrabold text-purple-700 dark:text-purple-300"
              >
                120
              </div>
              <div class="text-xs text-gray-600 dark:text-gray-400 mt-1">
                🥖 Baguettes
              </div>
            </div>
            <div
              class="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-3 text-center"
            >
              <div
                class="text-2xl font-extrabold text-purple-700 dark:text-purple-300"
              >
                45
              </div>
              <div class="text-xs text-gray-600 dark:text-gray-400 mt-1">
                🥐 Croissants
              </div>
            </div>
            <div
              class="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-3 text-center"
            >
              <div
                class="text-2xl font-extrabold text-purple-700 dark:text-purple-300"
              >
                30
              </div>
              <div class="text-xs text-gray-600 dark:text-gray-400 mt-1">
                🥪 Sandwiches
              </div>
            </div>
          </div>
          <div
            class="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-3 text-sm text-gray-600 dark:text-gray-400"
          >
            💡 <strong>Pourquoi ces quantités ?</strong> Lundi (-10%), pluie
            (-15%), centre-ville, basé sur vos 47 lundis précédents.
          </div>
        </div>
      }
    </div>
  </div>
</section>

<!-- ─── STATS ANIMÉES ─────────────────────────────────────────────────────── -->
<section
  class="stats-section py-16 border-b border-gray-100 dark:border-slate-700"
>
  <div class="max-w-6xl mx-auto px-4">
    <div class="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
      <div class="reveal">
        <div
          class="count-up text-4xl font-extrabold gradient-text"
          data-count="30"
          data-prefix="-"
          data-suffix="%"
        >
          -30%
        </div>
        <p class="text-gray-600 dark:text-gray-400 mt-2 text-sm">
          de pertes alimentaires
        </p>
      </div>
      <div class="reveal">
        <div
          class="count-up text-4xl font-extrabold gradient-text"
          data-count="15"
          data-prefix="+"
          data-suffix="%"
        >
          +15%
        </div>
        <p class="text-gray-600 dark:text-gray-400 mt-2 text-sm">
          de chiffre d'affaires
        </p>
      </div>
      <div class="reveal">
        <div
          class="count-up text-4xl font-extrabold gradient-text"
          data-count="2"
          data-suffix=" minutes"
        >
          2 minutes
        </div>
        <p class="text-gray-600 dark:text-gray-400 mt-2 text-sm">
          pour votre recommandation
        </p>
      </div>
      <div class="reveal">
        <div
          class="count-up text-4xl font-extrabold gradient-text"
          data-count="87"
          data-suffix="%"
        >
          87%
        </div>
        <p class="text-gray-600 dark:text-gray-400 mt-2 text-sm">
          de précision moyenne
        </p>
      </div>
    </div>
  </div>
</section>

<!-- ─── DÉMO VIDÉO ───────────────────────────────────────────────────────── -->
<section class="py-20 bg-white dark:bg-slate-800" id="demo">
  <div class="max-w-6xl mx-auto px-4">
    <div class="text-center mb-14 reveal">
      <h2
        class="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white"
      >
        Voyez <span class="gradient-text">par vous-même</span>
      </h2>
      <p
        class="text-lg text-gray-500 dark:text-gray-400 mt-3 max-w-2xl mx-auto"
      >
        Du « combien en préparer ? » à la bonne quantité : découvrez comment
        l'IA vous accompagne chaque jour.
      </p>
    </div>

    <!-- Vidéo chargée à l'approche de la section ; démo interactive si échec -->
    @if (demoVideoFailed()) {
      <app-demo-storyboard />
    } @else {
      @defer (on viewport; prefetch on idle) {
        <div class="max-w-4xl mx-auto">
          <app-video-player
            [src]="demoVideo.src"
            [poster]="demoVideo.poster"
            [label]="demoVideo.label"
            [captions]="demoVideo.captions"
            (loadError)="onDemoVideoError()"
          />
        </div>
      } @placeholder {
        <!-- Same height as the player (video + captions bar) to avoid layout shift -->
        <div class="max-w-4xl mx-auto overflow-hidden rounded-2xl bg-slate-900">
          <div class="aspect-video"></div>
          <div class="box-content h-14 border-t border-white/10"></div>
        </div>
      } @error {
        <app-demo-storyboard />
      }
    }

    <div class="text-center mt-10">
      <a
        routerLink="/register"
        class="inline-flex items-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-xl font-bold text-sm hover:bg-purple-700 transition shadow-lg shadow-purple-300/30"
      >
        🎁 Essai gratuit 1 mois · sans CB
      </a>
    </div>
  </div>
</section>

<!-- ─── PROBLÈME ──────────────────────────────────────────────────────────── -->
<section class="py-20 bg-gray-50 dark:bg-slate-900" id="probleme">
  <div class="max-w-7xl mx-auto px-4">
    <div class="text-center mb-14 reveal">
      <h2
        class="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white"
      >
        Vous perdez de l'argent <span class="text-red-500">chaque semaine</span>
      </h2>
      <p
        class="text-lg text-gray-500 dark:text-gray-400 mt-3 max-w-2xl mx-auto"
      >
        Que vous soyez en boutique ou sur les marchés, ces problèmes coûtent
        cher.
      </p>
    </div>

    <div class="grid md:grid-cols-3 gap-6">
      <div
        class="problem-card reveal bg-white dark:bg-slate-800 rounded-2xl p-8 border border-gray-100 dark:border-slate-700 hover:border-red-200 dark:border-red-800 transition-colors"
      >
        <div
          class="w-14 h-14 bg-red-50 dark:bg-red-900/20 rounded-xl flex items-center justify-center text-3xl mb-5"
        >
          📉
        </div>
        <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-3">
          Pertes alimentaires
        </h3>
        <p class="text-gray-600 dark:text-gray-400 leading-relaxed">
          15 à 40% de production jetée par peur de manquer. C'est
          <strong>200 à 600€/mois</strong> de marge qui partent à la poubelle.
        </p>
        <div class="mt-4 flex gap-2">
          <span
            class="text-xs bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-400 px-2.5 py-1 rounded-full"
            >🏪 Fixe</span
          >
          <span
            class="text-xs bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-400 px-2.5 py-1 rounded-full"
            >🚚 Itinérant</span
          >
        </div>
      </div>

      <div
        class="problem-card reveal bg-white dark:bg-slate-800 rounded-2xl p-8 border border-gray-100 dark:border-slate-700 hover:border-red-200 dark:border-red-800 transition-colors"
      >
        <div
          class="w-14 h-14 bg-red-50 dark:bg-red-900/20 rounded-xl flex items-center justify-center text-3xl mb-5"
        >
          🚫
        </div>
        <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-3">
          Ruptures de stock
        </h3>
        <p class="text-gray-600 dark:text-gray-400 leading-relaxed">
          Clients frustrés, ventes perdues, réputation en jeu. Vous ne saurez
          <strong>jamais combien vous avez perdu</strong>.
        </p>
        <div class="mt-4 flex gap-2">
          <span
            class="text-xs bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-400 px-2.5 py-1 rounded-full"
            >🏪 Fixe</span
          >
          <span
            class="text-xs bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-400 px-2.5 py-1 rounded-full"
            >🚚 Itinérant</span
          >
        </div>
      </div>

      <div
        class="problem-card reveal bg-white dark:bg-slate-800 rounded-2xl p-8 border border-gray-100 dark:border-slate-700 hover:border-red-200 dark:border-red-800 transition-colors"
      >
        <div
          class="w-14 h-14 bg-red-50 dark:bg-red-900/20 rounded-xl flex items-center justify-center text-3xl mb-5"
        >
          🎲
        </div>
        <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-3">
          Décisions au feeling
        </h3>
        <p class="text-gray-600 dark:text-gray-400 leading-relaxed">
          Excel, notes papier, mémoire approximative. Pas de données, pas de
          progrès. <strong>Vous refaites les mêmes erreurs</strong>.
        </p>
        <div class="mt-4 flex gap-2">
          <span
            class="text-xs bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-400 px-2.5 py-1 rounded-full"
            >🏪 Fixe</span
          >
          <span
            class="text-xs bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-400 px-2.5 py-1 rounded-full"
            >🚚 Itinérant</span
          >
        </div>
      </div>
    </div>
  </div>
</section>

<!-- ─── SOLUTION (Comment ça marche) ──────────────────────────────────────── -->
<section class="py-20 bg-white dark:bg-slate-800" id="fonctionnalites">
  <div class="max-w-7xl mx-auto px-4">
    <div class="text-center mb-14 reveal">
      <h2
        class="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white"
      >
        Comment <span class="gradient-text">Predivent</span> fonctionne
      </h2>
      <p
        class="text-lg text-gray-500 dark:text-gray-400 mt-3 max-w-2xl mx-auto"
      >
        3 étapes. 2 minutes. Des recommandations fiables dès le premier jour.
      </p>
    </div>

    <div class="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
      <!-- Step 1 -->
      <div class="reveal step-card text-center">
        <div class="step-number mx-auto">1</div>
        <div
          class="w-16 h-16 mx-auto bg-purple-50 dark:bg-purple-900/20 rounded-2xl flex items-center justify-center text-3xl mt-6 mb-4"
        >
          📋
        </div>
        <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-2">
          Décrivez votre journée
        </h3>
        <p class="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
          Date, lieu, type d'événement et horaires. Pour les commerces fixes,
          c'est encore plus simple : juste le jour.
        </p>
      </div>

      <!-- Step 2 -->
      <div class="reveal step-card text-center" style="animation-delay: 0.1s">
        <div class="step-number mx-auto">2</div>
        <div
          class="w-16 h-16 mx-auto bg-purple-50 dark:bg-purple-900/20 rounded-2xl flex items-center justify-center text-3xl mt-6 mb-4"
        >
          🧠
        </div>
        <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-2">
          L'IA calcule pour vous
        </h3>
        <p class="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
          Météo temps réel, historique de ventes, jour de la semaine, type de
          zone, événements locaux. Tout est combiné.
        </p>
      </div>

      <!-- Step 3 -->
      <div class="reveal step-card text-center" style="animation-delay: 0.2s">
        <div class="step-number mx-auto">3</div>
        <div
          class="w-16 h-16 mx-auto bg-purple-50 dark:bg-purple-900/20 rounded-2xl flex items-center justify-center text-3xl mt-6 mb-4"
        >
          📄
        </div>
        <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-2">
          Préparez & vendez
        </h3>
        <p class="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
          Liste de préparation optimisée, exportable et imprimable. Après la
          journée, saisissez vos ventes réelles pour affiner les prochaines
          recommandations.
        </p>
      </div>
    </div>

    <!-- Explainability callout -->
    <div
      class="reveal max-w-3xl mx-auto mt-14 bg-gradient-to-r from-purple-50 via-indigo-50 to-purple-50 rounded-2xl p-6 sm:p-8 border border-purple-100"
    >
      <div class="flex items-start gap-4">
        <div
          class="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center text-2xl flex-shrink-0"
        >
          🔍
        </div>
        <div>
          <h4 class="font-bold text-gray-900 dark:text-white mb-1">
            Chaque recommandation est expliquée
          </h4>
          <p class="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
            Pas de boîte noire. Predivent vous montre la formule :
            <span
              class="font-mono text-xs bg-white dark:bg-slate-800 px-2 py-1 rounded border border-gray-200 dark:border-slate-700 inline-block mt-1"
            >
              base × coefMétéo × coefJour × coefÉvénement × coefZone
            </span>
            <br />Vous comprenez <em>pourquoi</em> et gardez toujours le dernier
            mot.
          </p>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- ─── VALEUR PAR PROFIL (Fixe vs Itinérant) ─────────────────────────────── -->
<section class="py-20 bg-gray-50 dark:bg-slate-900" id="profils">
  <div class="max-w-7xl mx-auto px-4">
    <div class="text-center mb-14 reveal">
      <h2
        class="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white"
      >
        Une plateforme, <span class="gradient-text">deux modes</span>
      </h2>
      <p
        class="text-lg text-gray-500 dark:text-gray-400 mt-3 max-w-2xl mx-auto"
      >
        Predivent s'adapte à votre réalité. Les fonctionnalités sont les mêmes,
        le contexte change.
      </p>
    </div>

    <div class="grid md:grid-cols-2 gap-8">
      <!-- ITINERANT -->
      <div
        class="reveal profile-card bg-white dark:bg-slate-800 rounded-2xl p-8 border border-gray-100 dark:border-slate-700 hover:border-purple-200 dark:border-purple-800 transition-all"
      >
        <div class="flex items-center gap-3 mb-6">
          <span class="text-4xl">🚚</span>
          <div>
            <h3 class="text-2xl font-bold text-gray-900 dark:text-white">
              Itinérant
            </h3>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              Food trucks, marchés, stands, événements
            </p>
          </div>
        </div>
        <ul class="space-y-4">
          <li class="flex gap-3">
            <span
              class="w-8 h-8 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg flex items-center justify-center text-emerald-600 flex-shrink-0"
              >✓</span
            >
            <div>
              <p class="font-semibold text-gray-900 dark:text-white">
                Prédictions par événement
              </p>
              <p class="text-sm text-gray-500 dark:text-gray-400">
                Chaque lieu, chaque date, chaque météo est unique
              </p>
            </div>
          </li>
          <li class="flex gap-3">
            <span
              class="w-8 h-8 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg flex items-center justify-center text-emerald-600 flex-shrink-0"
              >✓</span
            >
            <div>
              <p class="font-semibold text-gray-900 dark:text-white">
                Découverte d'emplacements
              </p>
              <p class="text-sm text-gray-500 dark:text-gray-400">
                Lieux d'intérêt, événements locaux, zones de passage à proximité
              </p>
            </div>
          </li>
          <li class="flex gap-3">
            <span
              class="w-8 h-8 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg flex items-center justify-center text-emerald-600 flex-shrink-0"
              >✓</span
            >
            <div>
              <p class="font-semibold text-gray-900 dark:text-white">
                Mode kiosque terrain
              </p>
              <p class="text-sm text-gray-500 dark:text-gray-400">
                Saisie rapide des ventes sur mobile, même hors connexion
              </p>
            </div>
          </li>
          <li class="flex gap-3">
            <span
              class="w-8 h-8 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg flex items-center justify-center text-emerald-600 flex-shrink-0"
              >✓</span
            >
            <div>
              <p class="font-semibold text-gray-900 dark:text-white">
                Planning hebdomadaire
              </p>
              <p class="text-sm text-gray-500 dark:text-gray-400">
                Visualisez et planifiez vos journées de la semaine
              </p>
            </div>
          </li>
        </ul>
        <div class="mt-6 pt-6 border-t border-gray-100 dark:border-slate-700">
          <p class="text-sm font-semibold text-purple-700 dark:text-purple-300">
            💰 Économie moyenne : 1 500€/mois — rentabilisé dès la 1ère semaine
          </p>
        </div>
      </div>

      <!-- FIXE -->
      <div
        class="reveal profile-card bg-white dark:bg-slate-800 rounded-2xl p-8 border border-gray-100 dark:border-slate-700 hover:border-purple-200 dark:border-purple-800 transition-all"
      >
        <div class="flex items-center gap-3 mb-6">
          <span class="text-4xl">🏪</span>
          <div>
            <h3 class="text-2xl font-bold text-gray-900 dark:text-white">
              Commerce fixe
            </h3>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              Boulangeries, sandwicheries, bars, primeurs, glaciers
            </p>
          </div>
        </div>
        <ul class="space-y-4">
          <li class="flex gap-3">
            <span
              class="w-8 h-8 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg flex items-center justify-center text-emerald-600 flex-shrink-0"
              >✓</span
            >
            <div>
              <p class="font-semibold text-gray-900 dark:text-white">
                Prédictions quotidiennes
              </p>
              <p class="text-sm text-gray-500 dark:text-gray-400">
                Chaque lundi n'est pas un mardi. L'IA le sait.
              </p>
            </div>
          </li>
          <li class="flex gap-3">
            <span
              class="w-8 h-8 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg flex items-center justify-center text-emerald-600 flex-shrink-0"
              >✓</span
            >
            <div>
              <p class="font-semibold text-gray-900 dark:text-white">
                Saisonnalité automatique
              </p>
              <p class="text-sm text-gray-500 dark:text-gray-400">
                Vacances, jours fériés, rentrée — tout est intégré
              </p>
            </div>
          </li>
          <li class="flex gap-3">
            <span
              class="w-8 h-8 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg flex items-center justify-center text-emerald-600 flex-shrink-0"
              >✓</span
            >
            <div>
              <p class="font-semibold text-gray-900 dark:text-white">
                Alertes stock & catalogue
              </p>
              <p class="text-sm text-gray-500 dark:text-gray-400">
                Marge faible, prix manquant, stock bas — signalés en temps réel
              </p>
            </div>
          </li>
          <li class="flex gap-3">
            <span
              class="w-8 h-8 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg flex items-center justify-center text-emerald-600 flex-shrink-0"
              >✓</span
            >
            <div>
              <p class="font-semibold text-gray-900 dark:text-white">
                Benchmarks secteur
              </p>
              <p class="text-sm text-gray-500 dark:text-gray-400">
                Comparez vos performances aux commerces similaires
              </p>
            </div>
          </li>
        </ul>
        <div class="mt-6 pt-6 border-t border-gray-100 dark:border-slate-700">
          <p class="text-sm font-semibold text-purple-700 dark:text-purple-300">
            💰 Réduction pertes : jusqu'à -50% — +18% de marge nette
          </p>
        </div>
      </div>
    </div>

    <!-- Common features band -->
    <div
      class="reveal mt-10 bg-white dark:bg-slate-800 rounded-2xl p-6 border border-gray-100 dark:border-slate-700"
    >
      <p
        class="text-center text-sm font-semibold text-gray-500 dark:text-gray-400 mb-4"
      >
        FONCTIONNALITÉS COMMUNES AUX DEUX MODES
      </p>
      <div class="flex flex-wrap justify-center gap-3">
        <span class="feature-pill">☀️ Météo temps réel</span>
        <span class="feature-pill">📊 Statistiques & rapports</span>
        <span class="feature-pill">📱 Application mobile installable</span>
        <span class="feature-pill">📄 Export PDF</span>
        <span class="feature-pill">🔔 Alertes automatiques</span>
        <span class="feature-pill">🔒 Données protégées</span>
        <span class="feature-pill">💳 Paiement sécurisé</span>
        <span class="feature-pill">📈 Apprend de vos ventes</span>
        <span class="feature-pill">🌐 Fonctionne hors-ligne</span>
      </div>
    </div>
  </div>
</section>

<!-- ─── FONCTIONNALITÉS CLÉS ──────────────────────────────────────────────── -->
<section class="py-20 bg-white dark:bg-slate-800" id="features">
  <div class="max-w-7xl mx-auto px-4">
    <div class="text-center mb-14 reveal">
      <h2
        class="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white"
      >
        Tout ce qu'il faut pour <span class="gradient-text">vendre mieux</span>
      </h2>
    </div>

    <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
      <div class="reveal feature-card-v2">
        <div class="feature-icon-v2">🧠</div>
        <h3 class="font-bold text-gray-900 dark:text-white mb-2">
          Prévisions intelligentes
        </h3>
        <p class="text-sm text-gray-600 dark:text-gray-400">
          Quantités optimales par produit. Combine météo, vos ventes passées et
          le contexte local. 87% de précision moyenne.
        </p>
      </div>
      <div class="reveal feature-card-v2">
        <div class="feature-icon-v2">⛅</div>
        <h3 class="font-bold text-gray-900 dark:text-white mb-2">
          Météo intégrée
        </h3>
        <p class="text-sm text-gray-600 dark:text-gray-400">
          Prévisions en temps réel. L'IA ajuste automatiquement : soleil = +20%
          glaces, pluie = +15% boissons chaudes.
        </p>
      </div>
      <div class="reveal feature-card-v2">
        <div class="feature-icon-v2">📊</div>
        <h3 class="font-bold text-gray-900 dark:text-white mb-2">
          Suivi de votre activité
        </h3>
        <p class="text-sm text-gray-600 dark:text-gray-400">
          Chiffre d'affaires, marges, tendances, comparaisons. Visualisez votre
          progression et identifiez vos meilleurs produits.
        </p>
      </div>
      <div class="reveal feature-card-v2">
        <div class="feature-icon-v2">🏪</div>
        <h3 class="font-bold text-gray-900 dark:text-white mb-2">
          13 types de commerce
        </h3>
        <p class="text-sm text-gray-600 dark:text-gray-400">
          Boulangerie, food truck, bar, primeur, glacier, traiteur… Chaque
          métier a ses propres règles de calcul.
        </p>
      </div>
      <div class="reveal feature-card-v2">
        <div class="feature-icon-v2">📱</div>
        <h3 class="font-bold text-gray-900 dark:text-white mb-2">
          Mobile & offline
        </h3>
        <p class="text-sm text-gray-600 dark:text-gray-400">
          Installable sur votre téléphone comme une application. Saisie des
          ventes sur le terrain, même sans connexion. Tout se met à jour
          automatiquement.
        </p>
      </div>
      <div class="reveal feature-card-v2">
        <div class="feature-icon-v2">🔄</div>
        <h3 class="font-bold text-gray-900 dark:text-white mb-2">
          Boucle d'apprentissage
        </h3>
        <p class="text-sm text-gray-600 dark:text-gray-400">
          Après chaque journée, vos ventes réelles affinent les prédictions.
          Plus vous l'utilisez, plus c'est précis.
        </p>
      </div>
    </div>
  </div>
</section>

<!-- ─── CAS D'USAGE CONCRETS ──────────────────────────────────────────────── -->
<section class="py-20 bg-gray-50 dark:bg-slate-900" id="temoignages">
  <div class="max-w-7xl mx-auto px-4">
    <div class="text-center mb-14 reveal">
      <h2
        class="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white"
      >
        Conçu pour <span class="gradient-text">votre réalité</span>
      </h2>
      <p
        class="text-lg text-gray-500 dark:text-gray-400 mt-3 max-w-2xl mx-auto"
      >
        Voici ce que Predivent change concrètement au quotidien.
      </p>
    </div>

    <div class="grid md:grid-cols-3 gap-6">
      <div
        class="reveal testimonial-card-v2 bg-white dark:bg-slate-800 rounded-2xl p-6 border border-gray-100 dark:border-slate-700"
      >
        <div class="flex items-center gap-3 mb-4">
          <div
            class="w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center text-xl"
          >
            🚚
          </div>
          <div>
            <p class="font-bold text-gray-900 dark:text-white text-sm">
              Food truck burger
            </p>
            <p class="text-xs text-gray-500 dark:text-gray-400">
              Marchés & événements
            </p>
          </div>
        </div>
        <p class="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
          Samedi ensoleillé sur un marché de Noël ? L'IA recommande
          <strong>48 burgers</strong> au lieu de vos 30 habituels — en tenant
          compte du soleil, du samedi et du type d'événement.
        </p>
        <div
          class="mt-4 pt-4 border-t border-gray-100 dark:border-slate-700 text-xs text-purple-600 font-semibold"
        >
          💡 Résultat : moins de ruptures, plus de ventes
        </div>
      </div>

      <div
        class="reveal testimonial-card-v2 bg-white dark:bg-slate-800 rounded-2xl p-6 border border-gray-100 dark:border-slate-700"
      >
        <div class="flex items-center gap-3 mb-4">
          <div
            class="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-yellow-600 flex items-center justify-center text-xl"
          >
            🥖
          </div>
          <div>
            <p class="font-bold text-gray-900 dark:text-white text-sm">
              Boulangerie de quartier
            </p>
            <p class="text-xs text-gray-500 dark:text-gray-400">
              Commerce fixe • centre-ville
            </p>
          </div>
        </div>
        <p class="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
          Lundi pluvieux ? L'IA réduit les baguettes de 140 à
          <strong>110</strong> et augmente les sandwiches de 25 à
          <strong>35</strong>. Basé sur vos 47 lundis précédents.
        </p>
        <div
          class="mt-4 pt-4 border-t border-gray-100 dark:border-slate-700 text-xs text-purple-600 font-semibold"
        >
          💡 Résultat : moins de pertes, marge préservée
        </div>
      </div>

      <div
        class="reveal testimonial-card-v2 bg-white dark:bg-slate-800 rounded-2xl p-6 border border-gray-100 dark:border-slate-700"
      >
        <div class="flex items-center gap-3 mb-4">
          <div
            class="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-xl"
          >
            🧃
          </div>
          <div>
            <p class="font-bold text-gray-900 dark:text-white text-sm">
              Stand boissons
            </p>
            <p class="text-xs text-gray-500 dark:text-gray-400">
              Festivals & marchés
            </p>
          </div>
        </div>
        <p class="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
          Canicule annoncée sur un festival ? L'IA booste les boissons fraîches
          de <strong>+35%</strong> et réduit les boissons chaudes. Vous ne
          tombez plus en rupture à 15h.
        </p>
        <div
          class="mt-4 pt-4 border-t border-gray-100 dark:border-slate-700 text-xs text-purple-600 font-semibold"
        >
          💡 Résultat : stock adapté, zéro gaspillage
        </div>
      </div>
    </div>
  </div>
</section>

<!-- ─── PRICING ───────────────────────────────────────────────────────────── -->
<section class="py-20 bg-white dark:bg-slate-800" id="tarifs">
  <div class="max-w-7xl mx-auto px-4">
    <div class="text-center mb-14 reveal">
      <h2
        class="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white"
      >
        Tarification simple et transparente
      </h2>
      <p class="text-lg text-gray-500 dark:text-gray-400 mt-3">
        1 mois d'essai gratuit. Sans carte bancaire. Pause possible après
        souscription. Annuel : 2 mois offerts.
      </p>
      <div class="mt-5"><app-founder-banner /></div>
    </div>

    <div class="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
      <!-- Starter -->
      <div
        class="reveal pricing-card-v2 bg-white dark:bg-slate-800 rounded-2xl p-8 border border-gray-200 dark:border-slate-700"
      >
        <h3 class="text-lg font-bold text-gray-900 dark:text-white">Starter</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Parfait pour débuter
        </p>
        <div class="mt-6 mb-6">
          <span class="text-4xl font-extrabold text-gray-900 dark:text-white"
            >19,90€</span
          >
          <span class="text-gray-500 dark:text-gray-400">/mois</span>
        </div>
        <ul class="space-y-3 text-sm text-gray-700 dark:text-gray-300">
          <li class="flex items-center gap-2">
            <span class="text-emerald-500">✓</span> 10 événements / mois
            <span class="text-gray-400 text-xs">(itinérant)</span>
          </li>
          <li class="flex items-center gap-2">
            <span class="text-emerald-500">✓</span> Prédictions quotidiennes
            <span class="text-gray-400 text-xs">(fixe)</span>
          </li>
          <li class="flex items-center gap-2">
            <span class="text-emerald-500">✓</span> Recommandations
            intelligentes
          </li>
          <li class="flex items-center gap-2">
            <span class="text-emerald-500">✓</span> Météo temps réel
          </li>
          <li class="flex items-center gap-2">
            <span class="text-emerald-500">✓</span> Export PDF
          </li>
          <li class="flex items-center gap-2">
            <span class="text-emerald-500">✓</span> Support par email
          </li>
        </ul>
        <a
          routerLink="/register"
          [queryParams]="{ plan: 'starter' }"
          class="mt-8 block w-full text-center py-3 rounded-xl font-bold border-2 border-purple-600 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:bg-purple-900/20 transition"
        >
          Commencer l'essai
        </a>
      </div>

      <!-- Pro (featured) -->
      <div
        class="reveal pricing-card-v2 pricing-featured relative bg-gradient-to-b from-purple-600 to-indigo-700 rounded-2xl p-8 text-white shadow-xl dark:shadow-slate-900/60 scale-[1.03]"
      >
        <div class="absolute -top-3.5 left-1/2 -translate-x-1/2">
          <span
            class="bg-yellow-400 text-yellow-900 text-xs font-extrabold px-4 py-1.5 rounded-full shadow"
            >⭐ POPULAIRE</span
          >
        </div>
        <h3 class="text-lg font-bold">Pro</h3>
        <p class="text-sm text-purple-200 mt-1">Pour les pros actifs</p>
        <div class="mt-6 mb-6">
          <span class="text-4xl font-extrabold">29,90€</span>
          <span class="text-purple-200">/mois</span>
        </div>
        <ul class="space-y-3 text-sm">
          <li class="flex items-center gap-2">
            <span class="text-yellow-400">✓</span> Événements illimités
            <span class="text-purple-300 text-xs">(itinérant)</span>
          </li>
          <li class="flex items-center gap-2">
            <span class="text-yellow-400">✓</span> Tout Starter +
          </li>
          <li class="flex items-center gap-2">
            <span class="text-yellow-400">✓</span> Statistiques avancées
          </li>
          <li class="flex items-center gap-2">
            <span class="text-yellow-400">✓</span> Alertes automatiques
          </li>
          <li class="flex items-center gap-2">
            <span class="text-yellow-400">✓</span> Checklist de préparation
            <span class="text-purple-300 text-xs">(fixe)</span>
          </li>
          <li class="flex items-center gap-2">
            <span class="text-yellow-400">✓</span> Zone d'activité
            <span class="text-purple-300 text-xs">(itinérant)</span>
          </li>
          <li class="flex items-center gap-2">
            <span class="text-yellow-400">✓</span> Opportunités recommandées
            <span class="text-purple-300 text-xs">(itinérant)</span>
          </li>
          <li class="flex items-center gap-2">
            <span class="text-yellow-400">✓</span> Support prioritaire
          </li>
        </ul>
        <a
          routerLink="/register"
          [queryParams]="{ plan: 'pro' }"
          class="mt-8 block w-full text-center py-3 rounded-xl font-bold bg-white dark:bg-slate-800 text-purple-700 dark:text-purple-300 hover:bg-gray-100 dark:hover:bg-slate-700 dark:bg-slate-800 transition shadow-lg dark:shadow-slate-900/50"
        >
          Commencer l'essai
        </a>
      </div>

      <!-- Business -->
      <div
        class="reveal pricing-card-v2 bg-white dark:bg-slate-800 rounded-2xl p-8 border border-gray-200 dark:border-slate-700"
      >
        <h3 class="text-lg font-bold text-gray-900 dark:text-white">
          Business
        </h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Multi-stands & équipes
        </p>
        <div class="mt-6 mb-6">
          <span class="text-4xl font-extrabold text-gray-900 dark:text-white"
            >49,90€</span
          >
          <span class="text-gray-500 dark:text-gray-400">/mois</span>
        </div>
        <ul class="space-y-3 text-sm text-gray-700 dark:text-gray-300">
          <li class="flex items-center gap-2">
            <span class="text-emerald-500">✓</span> Tout Pro +
          </li>
          <li class="flex items-center gap-2">
            <span class="text-emerald-500">✓</span> Jusqu'à 5 stands
          </li>
          <li class="flex items-center gap-2">
            <span class="text-emerald-500">✓</span> Gestion d'équipe
          </li>
          <li class="flex items-center gap-2">
            <span class="text-emerald-500">✓</span> Comparaison avec votre
            secteur
          </li>
          <li class="flex items-center gap-2">
            <span class="text-emerald-500">✓</span> Accompagnement personnalisé
          </li>
        </ul>
        <a
          routerLink="/contact"
          class="mt-8 block w-full text-center py-3 rounded-xl font-bold border-2 border-purple-600 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:bg-purple-900/20 transition"
        >
          Nous contacter
        </a>
      </div>
    </div>

    <!-- Trust badges -->
    <div
      class="reveal flex flex-wrap justify-center gap-6 mt-10 text-sm text-gray-400"
    >
      <span>🔒 Paiement sécurisé</span>
      <span>💳 Sans engagement</span>
      <span>🇫🇷 Données hébergées en France</span>
    </div>
  </div>
</section>

<!-- ─── FAQ ────────────────────────────────────────────────────────────────── -->
<section class="py-20 bg-gray-50 dark:bg-slate-900" id="faq">
  <div class="max-w-3xl mx-auto px-4">
    <div class="text-center mb-14 reveal">
      <h2
        class="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white"
      >
        Questions fréquentes
      </h2>
    </div>

    <div class="space-y-3">
      @for (item of faqs; track item.q; let i = $index) {
        <div
          class="reveal faq-item bg-white dark:bg-slate-800 rounded-xl border border-gray-100 dark:border-slate-700 overflow-hidden"
        >
          <button
            (click)="toggleFaq(i)"
            class="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 dark:hover:bg-slate-700 dark:bg-slate-900 transition"
          >
            <span class="font-semibold text-gray-900 dark:text-white">{{
              item.q
            }}</span>
            <svg
              [class]="openFaq() === i ? 'rotate-180' : ''"
              class="w-5 h-5 text-gray-400 transform transition-transform duration-300 flex-shrink-0 ml-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
          @if (openFaq() === i) {
            <div
              class="px-5 pb-5 text-sm text-gray-600 dark:text-gray-400 leading-relaxed animate-fadeIn"
            >
              {{ item.a }}
            </div>
          }
        </div>
      }
    </div>
  </div>
</section>

<!-- ─── CTA FINAL ─────────────────────────────────────────────────────────── -->
<section class="py-20 relative overflow-hidden">
  <div
    class="absolute inset-0 bg-gradient-to-br from-purple-700 via-indigo-700 to-purple-800"
  ></div>
  <div class="absolute inset-0 opacity-10" aria-hidden="true">
    <div
      class="absolute top-10 left-10 w-72 h-72 bg-white dark:bg-slate-800 rounded-full filter blur-3xl"
    ></div>
    <div
      class="absolute bottom-10 right-10 w-96 h-96 bg-white dark:bg-slate-800 rounded-full filter blur-3xl"
    ></div>
  </div>

  <div class="relative z-10 max-w-4xl mx-auto px-4 text-center">
    <h2
      class="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white mb-6 leading-tight"
    >
      Arrêtez de jeter.<br />Commencez à optimiser.
    </h2>
    <p class="text-lg text-purple-200 mb-10 max-w-2xl mx-auto">
      Réduisez vos pertes, évitez les ruptures et augmentez votre marge — dès la
      première semaine.
    </p>
    <div class="flex justify-center">
      <a
        routerLink="/register"
        class="cta-primary inline-flex items-center justify-center gap-2 bg-white text-purple-700 px-10 py-4 rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl transform hover:-translate-y-0.5 transition-all duration-300"
      >
        🚀 Essai gratuit 1 mois
      </a>
    </div>
    <div
      class="flex flex-wrap justify-center gap-6 mt-8 text-sm text-purple-200"
    >
      <span>✓ Sans CB</span>
      <span>✓ Setup en 5 min</span>
      <span>✓ Support 7j/7</span>
    </div>
  </div>
</section>
